/**
 * Email Queue Admin Page
 * Permission: EMAIL_QUEUE_VIEW (view + retry) — SUPER_ADMIN to cancel
 */
import { useState } from "react";
import { RefreshCw, XCircle } from "lucide-react";
import { emailQueueApi } from "./emailQueueApi";
import type { EmailQueueStatus } from "./emailQueueApi";
import { useAsyncData } from "#/features/inventory/hooks";
import { useAuth } from "#/context/AuthProvider";

const STATUS_OPTS: { label: string; value: EmailQueueStatus | "" }[] = [
  { label: "All", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Processing", value: "PROCESSING" },
  { label: "Sent", value: "SENT" },
  { label: "Failed", value: "FAILED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const statusCls: Record<string, string> = {
  PENDING:    "bg-amber-50 text-amber-700 border border-amber-200",
  PROCESSING: "bg-blue-50 text-blue-700 border border-blue-200",
  SENT:       "bg-emerald-100 text-emerald-700",
  FAILED:     "bg-red-100 text-red-700",
  CANCELLED:  "bg-slate-100 text-slate-500",
};

function fmtDate(s?: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" });
}

export default function EmailQueuePage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole("SUPER_ADMIN");

  const [status, setStatus] = useState<EmailQueueStatus | "">("");
  const [page, setPage] = useState(0);
  const [acting, setActing] = useState<number | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const { data, loading, error, reload } = useAsyncData(
    () => emailQueueApi.list({ page, size: 25, status: status || undefined }),
    [page, status],
  );

  // Normalise: API may return Page<T> with .content, or a plain array
  const rows = data?.content ?? (Array.isArray(data) ? data : []);
  const totalPages: number = data?.totalPages ?? 1;

  async function doRetry(id: number) {
    setActing(id); setActionErr(null);
    try { await emailQueueApi.retry(id); reload(); }
    catch (e: unknown) { setActionErr(String((e as { message?: string })?.message ?? e)); }
    setActing(null);
  }

  async function doCancel(id: number) {
    setActing(id); setActionErr(null);
    try { await emailQueueApi.cancel(id); reload(); }
    catch (e: unknown) { setActionErr(String((e as { message?: string })?.message ?? e)); }
    setActing(null);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Workflow Engine</p>
        <h1 className="text-xl font-bold text-slate-900">Email Queue</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={status}
          onChange={e => { setStatus(e.target.value as EmailQueueStatus | ""); setPage(0); }}
          className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          {STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={reload} className="ml-auto flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {actionErr && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{actionErr}</div>
      )}

      {/* Table */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
        {loading && <div className="px-5 py-8 text-center text-sm text-slate-400">Loading email queue…</div>}
        {error   && <div className="px-5 py-4 text-sm text-red-600">{error}</div>}

        {!loading && data && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">To</th>
                    <th className="px-4 py-3 text-left">Subject</th>
                    <th className="px-4 py-3 text-left">Event</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Attempts</th>
                    <th className="px-4 py-3 text-left">Scheduled</th>
                    <th className="px-4 py-3 text-left">Error</th>
                    <th className="px-4 py-3 w-28"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-slate-400">No emails match your filter.</td></tr>
                  )}
                  {rows.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">
                        {item.toAddresses ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-800 max-w-[200px] truncate">{item.subject}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{item.eventType ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusCls[item.status] ?? "bg-slate-100 text-slate-500"}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{item.attempts}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(item.scheduledFor)}</td>
                      <td className="px-4 py-3 text-xs text-red-500 max-w-[200px] truncate" title={item.lastError ?? ""}>
                        {item.lastError ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          {item.status === "FAILED" && (
                            <button
                              onClick={() => doRetry(item.id)}
                              disabled={acting === item.id}
                              title="Retry"
                              className="rounded-lg border border-emerald-300 p-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {isSuperAdmin && item.status === "PENDING" && (
                            <button
                              onClick={() => doCancel(item.id)}
                              disabled={acting === item.id}
                              title="Cancel"
                              className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-50"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-5 py-3 text-sm text-slate-500">
                <span>{data?.totalElements ?? rows.length} total</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                    className="rounded-lg border px-3 py-1 disabled:opacity-40 hover:bg-slate-50">← Prev</button>
                  <span className="px-2 py-1">Page {page + 1} of {totalPages}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}
                    className="rounded-lg border px-3 py-1 disabled:opacity-40 hover:bg-slate-50">Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
